import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AppLayout from '@/components/AppLayout';
import { API } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  ArrowLeft,
  RefreshCw,
  MessageSquare,
  AlertCircle,
  CheckCircle,
  Clock,
  Copy,
  Sparkles,
  FileText,
  HelpCircle,
  UserRound,
  AtSign,
  Trash2,
  CalendarDays,
  Bot,
  ChevronsUpDown,
  Check,
  Stars,
  Upload
} from 'lucide-react';

const normalizeSuggestionOptions = (options) => {
  if (Array.isArray(options)) {
    return options.map((option, index) => {
      if (typeof option === 'string') {
        const optionTypes = ['curta', 'objetiva', 'acolhedora'];
        return { type: optionTypes[index] || 'resposta', text: option };
      }
      return {
        type: option?.type || 'resposta',
        text: option?.text || option?.response || '',
      };
    }).filter((option) => option.text);
  }

  if (options && typeof options === 'object') {
    return ['curta', 'objetiva', 'acolhedora']
      .map((type) => ({ type, text: options[type] || '' }))
      .filter((option) => option.text);
  }

  return [];
};

const dedupeIdentityItems = (items) => {
  const seen = new Set();
  return (items || []).filter((item) => {
    const normalized = String(item || '').trim().toLowerCase();
    if (!normalized || seen.has(normalized)) {
      return false;
    }
    seen.add(normalized);
    return true;
  });
};

const isLimitError = (detail, code) => {
  if (detail?.code === code) {
    return true;
  }

  const message = typeof detail === 'string'
    ? detail
    : detail?.message || detail?.detail || '';

  const normalized = String(message || '').toLowerCase();
  return normalized.includes('limite') || normalized.includes('limit');
};

const safeReadJson = async (response) => {
  try {
    return await response.json();
  } catch (error) {
    return null;
  }
};

const getApiErrorMessage = (detail, fallbackMessage) => {
  if (!detail) return fallbackMessage;

  if (typeof detail === 'string') {
    return detail;
  }

  if (detail?.message) {
    if (detail.retryable) {
      return `${detail.message} Tente novamente em instantes.`;
    }
    return detail.message;
  }

  if (detail?.detail) {
    return detail.detail;
  }

  return fallbackMessage;
};

const formatDateLabel = (value) => {
  if (!value) return 'Data não informada';
  const [year, month, day] = String(value).split('-');
  if (!year || !month || !day) return value;
  return `${day}/${month}/${year}`;
};

const formatDateTimeLabel = (value) => {
  if (!value) return 'Agora há pouco';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export default function GroupDetail() {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const [group, setGroup] = useState(null);
  const [summary, setSummary] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summarizing, setSummarizing] = useState(false);
  const [selectedParticipants, setSelectedParticipants] = useState([]);
  const [extraAliases, setExtraAliases] = useState('');
  const [savingIdentity, setSavingIdentity] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [deletingGroup, setDeletingGroup] = useState(false);
  const [askPrompt, setAskPrompt] = useState('');
  const [askingAi, setAskingAi] = useState(false);
  const [groupAnswer, setGroupAnswer] = useState(null);
  const [groupAnswerError, setGroupAnswerError] = useState('');
  const [participantsOpen, setParticipantsOpen] = useState(false);
  const [identityOnlyAsk, setIdentityOnlyAsk] = useState(false);
  const [askHistory, setAskHistory] = useState([]);
  const [suggestedQuestions, setSuggestedQuestions] = useState([]);
  const [askTab, setAskTab] = useState('ask');
  const [mentionsOpen, setMentionsOpen] = useState(false);
  const [mentionsLoading, setMentionsLoading] = useState(false);
  const [mentionsData, setMentionsData] = useState({ mentions: [], mentions_count: 0 });
  const [usage, setUsage] = useState(null);
  const [summaryLimitDialogOpen, setSummaryLimitDialogOpen] = useState(false);
  const [askLimitDialogOpen, setAskLimitDialogOpen] = useState(false);
  const [summaryHistory, setSummaryHistory] = useState([]);
  const [summaryTab, setSummaryTab] = useState('current');

  const refreshUsageAndOpenLimitDialog = useCallback(async (feature) => {
    try {
      const response = await fetch(`${API}/groups/${groupId}/usage`, {
        credentials: 'include',
      });

      if (!response.ok) {
        return false;
      }

      const usageData = await response.json();
      setUsage(usageData);

      const featureUsage = usageData?.[feature];
      if (!featureUsage || featureUsage.is_unlimited) {
        return false;
      }

      const todayReached = Number(featureUsage.today_used || 0) >= Number(featureUsage.today_limit || 0) && Number(featureUsage.today_limit || 0) > 0;
      const monthReached = Number(featureUsage.month_used || 0) >= Number(featureUsage.month_limit || 0) && Number(featureUsage.month_limit || 0) > 0;

      if (todayReached || monthReached) {
        if (feature === 'summary') {
          setSummaryLimitDialogOpen(true);
        } else if (feature === 'ask') {
          setAskLimitDialogOpen(true);
        }
        return true;
      }
    } catch (error) {
      console.error(`Error refreshing ${feature} usage:`, error);
    }

    return false;
  }, [groupId]);

  const fetchData = useCallback(async () => {
    try {
      const summaryUrl = selectedDate ? `${API}/groups/${groupId}/summary?date=${selectedDate}` : `${API}/groups/${groupId}/summary`;
      const messagesUrl = selectedDate ? `${API}/groups/${groupId}/messages?date=${selectedDate}` : `${API}/groups/${groupId}/messages`;
      const usageUrl = `${API}/groups/${groupId}/usage`;
      const summaryHistoryUrl = `${API}/groups/${groupId}/summary-history`;
      const [groupRes, summaryRes, messagesRes, usageRes] = await Promise.all([
        fetch(`${API}/groups/${groupId}`, { credentials: 'include' }),
        fetch(summaryUrl, { credentials: 'include' }),
        fetch(messagesUrl, { credentials: 'include' }),
        fetch(usageUrl, { credentials: 'include' }),
      ]);

      if (groupRes.ok) {
        const groupData = await groupRes.json();
        setGroup(groupData);
        const identityItems = [
          groupData.owner_identity?.primary_name,
          ...(groupData.owner_identity?.aliases || []),
        ].filter(Boolean);
        const normalizedIdentityItems = dedupeIdentityItems(identityItems);
        setSelectedParticipants(normalizedIdentityItems.filter((item) => groupData.participants?.includes(item)));
        setExtraAliases(normalizedIdentityItems.filter((item) => !groupData.participants?.includes(item)).join(', '));
        setSelectedDate((currentDate) => currentDate || groupData.latest_message_date || '');
      }
      if (summaryRes.ok) {
        setSummary(await summaryRes.json());
      } else if (summaryRes.status === 404) {
        setSummary(null);
      }
      if (messagesRes.ok) {
        setMessages(await messagesRes.json());
      }
      if (usageRes.ok) {
        setUsage(await usageRes.json());
      }
      const [historyRes, suggestedRes, summaryHistoryRes] = await Promise.all([
        fetch(`${API}/groups/${groupId}/ask-history`, { credentials: 'include' }),
        fetch(`${API}/groups/${groupId}/suggested-questions`, { credentials: 'include' }),
        fetch(summaryHistoryUrl, { credentials: 'include' }),
      ]);
      if (historyRes.ok) {
        setAskHistory(await historyRes.json());
      }
      if (suggestedRes.ok) {
        const suggestedData = await suggestedRes.json();
        setSuggestedQuestions(suggestedData.suggestions || []);
      }
      if (summaryHistoryRes.ok) {
        setSummaryHistory(await summaryHistoryRes.json());
      }
      setGroupAnswer(null);
    } catch (error) {
      console.error('Error fetching group:', error);
      toast.error('Erro ao carregar dados do grupo');
    } finally {
      setLoading(false);
    }
  }, [groupId, selectedDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSaveIdentity = async () => {
    const aliasesFromInput = extraAliases.split(',').map((item) => item.trim()).filter(Boolean);
    const identityItems = dedupeIdentityItems([...selectedParticipants, ...aliasesFromInput]);

    if (identityItems.length === 0) {
      toast.error('Selecione pelo menos um nome que representa você neste grupo');
      return;
    }

    setSavingIdentity(true);
    try {
      let summaryRegenerated = true;
      const response = await fetch(`${API}/groups/${groupId}/identity`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          primary_name: identityItems[0],
          aliases: identityItems.slice(1),
        }),
      });

      if (response.ok) {
        const updatedGroup = await response.json();
        setGroup(updatedGroup);
        const updatedIdentityItems = [
          updatedGroup.owner_identity?.primary_name,
          ...(updatedGroup.owner_identity?.aliases || []),
        ].filter(Boolean);
        const normalizedUpdatedIdentityItems = dedupeIdentityItems(updatedIdentityItems);
        setSelectedParticipants(normalizedUpdatedIdentityItems.filter((item) => updatedGroup.participants?.includes(item)));
        setExtraAliases(normalizedUpdatedIdentityItems.filter((item) => !updatedGroup.participants?.includes(item)).join(', '));
        const summarizeUrl = selectedDate
          ? `${API}/groups/${groupId}/summarize?date=${selectedDate}`
          : `${API}/groups/${groupId}/summarize`;
        const summaryResponse = await fetch(summarizeUrl, {
          method: 'POST',
          credentials: 'include'
        });
        if (summaryResponse.ok) {
          const summaryData = await summaryResponse.json();
          setSummary(summaryData);
          setSummaryHistory((current) => [
            summaryData,
            ...current.filter((item) => (item.history_id || item.summary_id) !== (summaryData.history_id || summaryData.summary_id)),
          ]);
          if (summaryData.usage) {
            setUsage((current) => ({ ...current, ...summaryData.usage, plan: current?.plan }));
          }
        } else {
          const error = await summaryResponse.json();
          const detail = error.detail;
          if (isLimitError(detail, 'SUMMARY_LIMIT_REACHED')) {
            setSummaryLimitDialogOpen(true);
            toast.error(detail?.message || detail || 'Limite de resumos atingido');
            summaryRegenerated = false;
          }
        }
        await fetchData();
        toast.success(summaryRegenerated ? 'Identidade do grupo atualizada' : 'Identidade salva, mas o resumo bateu no limite do Starter');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erro ao salvar identidade');
      }
    } catch (error) {
      toast.error('Erro ao conectar com o servidor');
    } finally {
      setSavingIdentity(false);
    }
  };

  const handleToggleParticipant = (participant, checked) => {
    setSelectedParticipants((current) =>
      checked
        ? dedupeIdentityItems([...current, participant])
        : current.filter((item) => item !== participant)
    );
  };

  const handleDeleteGroup = async () => {
    setDeletingGroup(true);
    try {
      const response = await fetch(`${API}/groups/${groupId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        toast.success('Grupo e dados importados removidos');
        navigate('/app', { replace: true });
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erro ao excluir grupo');
      }
    } catch (error) {
      toast.error('Erro ao conectar com o servidor');
    } finally {
      setDeletingGroup(false);
    }
  };

  const handleGenerateSummary = async () => {
    setSummarizing(true);
    try {
      const blockedByCurrentUsage = await refreshUsageAndOpenLimitDialog('summary');
      if (blockedByCurrentUsage) {
        toast.error('Você atingiu o limite de resumos do plano Starter');
        return;
      }

      const summarizeUrl = selectedDate ? `${API}/groups/${groupId}/summarize?date=${selectedDate}` : `${API}/groups/${groupId}/summarize`;
      const response = await fetch(summarizeUrl, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        const newSummary = await response.json();
        setSummary(newSummary);
        setSummaryHistory((current) => [
          newSummary,
          ...current.filter((item) => (item.history_id || item.summary_id) !== (newSummary.history_id || newSummary.summary_id)),
        ]);
        if (newSummary.usage) {
          setUsage((current) => ({ ...current, ...newSummary.usage, plan: current?.plan }));
        }
        toast.success('Resumo gerado com sucesso!');
      } else {
        const error = await safeReadJson(response);
        const detail = error?.detail;
        if (isLimitError(detail, 'SUMMARY_LIMIT_REACHED')) {
          setSummaryLimitDialogOpen(true);
          toast.error(detail?.message || detail || 'Limite de resumos atingido');
        } else {
          const openedByUsage = await refreshUsageAndOpenLimitDialog('summary');
          if (openedByUsage) {
            toast.error('Você atingiu o limite de resumos do plano Starter');
          } else {
            toast.error(getApiErrorMessage(detail, 'Erro ao gerar resumo'));
          }
        }
      }
    } catch (error) {
      const openedByUsage = await refreshUsageAndOpenLimitDialog('summary');
      if (openedByUsage) {
        toast.error('Você atingiu o limite de resumos do plano Starter');
      } else {
        toast.error(error?.message || 'Erro ao conectar com o servidor');
      }
    } finally {
      setSummarizing(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiado para a área de transferência!');
  };

  const handleAskGroup = async (questionOverride) => {
    const incomingQuestion = typeof questionOverride === 'string' ? questionOverride : askPrompt;
    const finalQuestion = incomingQuestion.trim();

    if (!finalQuestion) {
      toast.error('Escreva uma pergunta para a IA');
      return;
    }

    if (questionOverride) {
      setAskPrompt(finalQuestion);
    }

    setAskingAi(true);
    setGroupAnswerError('');
    try {
      const blockedByCurrentUsage = await refreshUsageAndOpenLimitDialog('ask');
      if (blockedByCurrentUsage) {
        toast.error('Você atingiu o limite de perguntas da Openova.IA no Starter');
        return;
      }

      const response = await fetch(`${API}/groups/${groupId}/ask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          question: finalQuestion,
          date: selectedDate || undefined,
          identity_only: identityOnlyAsk,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setGroupAnswer(data);
        if (data.usage) {
          setUsage((current) => ({ ...current, ...data.usage, plan: current?.plan }));
        }
        setAskHistory((current) => [data, ...current].slice(0, 30));
      } else {
        const error = await safeReadJson(response);
        const detail = error?.detail;
        if (isLimitError(detail, 'ASK_LIMIT_REACHED')) {
          setAskLimitDialogOpen(true);
          toast.error(detail?.message || detail || 'Limite de perguntas atingido');
        } else {
          const openedByUsage = await refreshUsageAndOpenLimitDialog('ask');
          if (openedByUsage) {
            toast.error('Você atingiu o limite de perguntas da Openova.IA no Starter');
          } else {
            const message = getApiErrorMessage(detail, 'Erro ao perguntar para a IA');
            setGroupAnswer(null);
            setGroupAnswerError(message);
            toast.error(message);
          }
        }
      }
    } catch (error) {
      const openedByUsage = await refreshUsageAndOpenLimitDialog('ask');
      if (openedByUsage) {
        toast.error('Você atingiu o limite de perguntas da Openova.IA no Starter');
      } else {
        const message = error?.message || 'Erro ao conectar com o servidor';
        setGroupAnswer(null);
        setGroupAnswerError(message);
        toast.error(message);
      }
    } finally {
      setAskingAi(false);
    }
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      high: 'priority-high border',
      medium: 'priority-medium border',
      low: 'priority-low border'
    };
    const labels = {
      high: 'Alta',
      medium: 'Média',
      low: 'Baixa'
    };
    return (
      <Badge className={`${styles[priority]} text-xs`}>
        {labels[priority]}
      </Badge>
    );
  };

  const loadMentions = async () => {
    setMentionsLoading(true);
    try {
      const response = await fetch(`${API}/groups/${groupId}/mentions`, {
        credentials: 'include',
      });

      if (response.ok) {
        setMentionsData(await response.json());
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erro ao carregar mensagens citadas');
      }
    } catch (error) {
      toast.error('Erro ao conectar com o servidor');
    } finally {
      setMentionsLoading(false);
    }
  };

  const handleOpenMentions = async () => {
    setMentionsOpen(true);
    await loadMentions();
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="p-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 w-48 bg-zinc-200 rounded" />
            <div className="h-32 bg-zinc-200 rounded-xl" />
            <div className="h-64 bg-zinc-200 rounded-xl" />
          </div>
        </div>
      </AppLayout>
    );
  }

  if (!group) {
    return (
      <AppLayout>
        <div className="p-8 text-center">
          <h2 className="text-xl font-semibold text-zinc-900">Grupo não encontrado</h2>
          <Button onClick={() => navigate('/app')} className="mt-4">
            Voltar ao Dashboard
          </Button>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="p-4 md:p-8 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col gap-4 mb-6 md:mb-8 md:flex-row md:items-center">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate('/app')}
            className="rounded-full self-start"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <div className="flex flex-col items-start gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-zinc-900 break-words">
                {group.name}
              </h1>
              <Badge variant="outline" className="bg-zinc-100 text-zinc-600 border-0">
                {group.category}
              </Badge>
            </div>
            <p className="text-sm md:text-base text-zinc-500 mt-1 leading-relaxed">
              {group.message_count} mensagens • {group.needs_response} precisam de resposta •{' '}
              <button
                type="button"
                onClick={handleOpenMentions}
                className="font-medium text-violet-700 hover:text-violet-900 hover:underline"
              >
                {group.directed_to_user_count || 0} citaram você
              </button>
            </p>
          </div>
          <div className="flex w-full flex-col gap-3 sm:flex-row sm:flex-wrap sm:justify-end md:w-auto md:flex-row md:items-center">
            <Button
              variant="outline"
              className="rounded-full w-full sm:w-auto"
              onClick={() => navigate(`/app/import?groupName=${encodeURIComponent(group.name)}`)}
            >
              <Upload className="w-4 h-4 mr-2" />
              Atualizar Conversa
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" className="rounded-full text-red-600 border-red-200 hover:bg-red-50 w-full sm:w-auto">
                  <Trash2 className="w-4 h-4 mr-2" />
                  Excluir Grupo
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Excluir grupo importado?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Isso vai remover o grupo, mensagens e resumos já importados deste grupo.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDeleteGroup} disabled={deletingGroup} className="bg-red-600 hover:bg-red-700">
                    {deletingGroup ? 'Excluindo...' : 'Excluir'}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            <Button
              onClick={handleGenerateSummary}
              disabled={summarizing}
              className="rounded-full w-full sm:w-auto"
              data-testid="generate-summary-btn"
            >
              {summarizing ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Gerando...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Gerar Novas Sugestões
                </>
              )}
            </Button>
          </div>
        </div>

        <Sheet open={mentionsOpen} onOpenChange={setMentionsOpen}>
          <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto">
            <SheetHeader>
              <SheetTitle>Mensagens que citaram você</SheetTitle>
              <SheetDescription>
                Veja as mensagens ligadas à identidade escolhida, com data e indicação se exigem alguma ação.
              </SheetDescription>
            </SheetHeader>

            <div className="mt-6 space-y-4">
              {mentionsLoading ? (
                <div className="text-sm text-zinc-500">Carregando menções...</div>
              ) : mentionsData.mentions?.length > 0 ? (
                mentionsData.mentions.map((message, index) => (
                  <div
                    key={message.message_id || index}
                    className={`rounded-xl border p-4 ${
                      message.needs_action
                        ? 'border-orange-200 bg-orange-50'
                        : 'border-violet-200 bg-violet-50'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div>
                        <p className="text-sm font-medium text-zinc-900">{message.sender}</p>
                        <p className="text-xs text-zinc-500">{message.date}</p>
                      </div>
                      <Badge className={`border-0 text-xs ${
                        message.needs_action
                          ? 'bg-orange-100 text-orange-700'
                          : 'bg-zinc-100 text-zinc-700'
                      }`}>
                        {message.action_label}
                      </Badge>
                    </div>
                    <p className="text-sm text-zinc-700">{message.content}</p>
                  </div>
                ))
              ) : (
                <div className="rounded-xl border border-zinc-200 bg-zinc-50 p-4 text-sm text-zinc-500">
                  Nenhuma mensagem direcionada à identidade escolhida foi encontrada neste grupo.
                </div>
              )}
            </div>
          </SheetContent>
        </Sheet>

        {summaryLimitDialogOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4">
            <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
              <h3 className="text-lg font-semibold text-zinc-900">Limite de resumos do Starter atingido</h3>
              <p className="mt-2 text-sm text-zinc-600">
                No plano Starter você pode gerar até 2 resumos por dia em cada grupo e até 120 resumos no mês. Faça upgrade para continuar usando mais resumos.
              </p>
              <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                <Button variant="outline" onClick={() => setSummaryLimitDialogOpen(false)}>
                  Agora não
                </Button>
                <Button onClick={() => navigate('/app/billing')}>
                  Fazer upgrade
                </Button>
              </div>
            </div>
          </div>
        )}

        {askLimitDialogOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4">
            <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
              <h3 className="text-lg font-semibold text-zinc-900">Limite de perguntas da IA atingido</h3>
              <p className="mt-2 text-sm text-zinc-600">
                No plano Starter você pode fazer até 3 perguntas por dia para a Openova.IA em cada grupo, com limite mensal de 90 perguntas. Faça upgrade para continuar perguntando.
              </p>
              <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                <Button variant="outline" onClick={() => setAskLimitDialogOpen(false)}>
                  Agora não
                </Button>
                <Button onClick={() => navigate('/app/billing')}>
                  Fazer upgrade
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="grid lg:grid-cols-12 gap-6">
          {/* Left Column - Summary */}
          <div className="lg:col-span-8 space-y-6">
            <Card className="border-zinc-200">
              <CardHeader className="border-b border-zinc-100">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <CalendarDays className="w-5 h-5 text-violet-600" />
                  Data da Análise
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 md:p-6">
                <div className="max-w-sm">
                  <Label>Escolha o dia da conversa</Label>
                  <div className="mt-2 flex flex-col gap-3 sm:flex-row sm:items-center">
                    <Select value={selectedDate} onValueChange={setSelectedDate}>
                      <SelectTrigger className="flex-1">
                        <SelectValue placeholder="Selecione uma data" />
                      </SelectTrigger>
                      <SelectContent>
                        {(group?.available_dates || []).map((date) => (
                          <SelectItem key={date} value={date}>
                            {date}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      onClick={handleGenerateSummary}
                      disabled={summarizing}
                      className="rounded-full whitespace-nowrap w-full sm:w-auto"
                    >
                      {summarizing ? 'Gerando...' : 'Gerar resumo'}
                    </Button>
                  </div>
                  <p className="text-sm text-zinc-500 mt-2">
                    Por padrão, o sistema analisa o último dia detectado na conversa importada.
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-zinc-200">
              <CardHeader className="border-b border-zinc-100">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <UserRound className="w-5 h-5 text-violet-600" />
                  Sua Identidade Neste Grupo
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 md:p-6 space-y-4">
                <div>
                  <Label className="text-sm font-medium text-zinc-700">Participantes detectados na conversa</Label>
                  <Popover open={participantsOpen} onOpenChange={setParticipantsOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="mt-2 w-full justify-between rounded-2xl border-zinc-200 h-auto min-h-[52px] px-4 py-3 text-left"
                      >
                        <div className="flex flex-wrap gap-2 pr-3">
                          {selectedParticipants.length > 0 ? (
                            selectedParticipants.map((participant) => (
                              <Badge key={participant} className="bg-violet-100 text-violet-700 border-0">
                                {participant}
                              </Badge>
                            ))
                          ) : (
                            <span className="text-sm text-zinc-500">Escolha um ou mais nomes seus no grupo</span>
                          )}
                        </div>
                        <ChevronsUpDown className="w-4 h-4 text-zinc-400 flex-shrink-0" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[calc(100vw-2rem)] max-w-[360px] p-0" align="start">
                      <Command>
                        <CommandInput placeholder="Buscar participante..." />
                        <CommandList>
                          <CommandEmpty>Nenhum participante encontrado.</CommandEmpty>
                          <CommandGroup>
                            {(group.participants || []).map((participant) => (
                              <CommandItem
                                key={participant}
                                onSelect={() => handleToggleParticipant(participant, !selectedParticipants.includes(participant))}
                                className="py-3"
                              >
                                <Checkbox
                                  checked={selectedParticipants.includes(participant)}
                                  onCheckedChange={(checked) => handleToggleParticipant(participant, Boolean(checked))}
                                />
                                <span className="flex-1">{participant}</span>
                                {selectedParticipants.includes(participant) && (
                                  <Check className="w-4 h-4 text-violet-600" />
                                )}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  <p className="text-sm text-zinc-500 mt-2">
                    Selecione uma ou mais formas como voce aparece na conversa, sem poluir a tela com a lista inteira aberta.
                  </p>
                </div>

                <div>
                  <Label htmlFor="extraAliases">Alias extras, se houver</Label>
                  <Input
                    id="extraAliases"
                    value={extraAliases}
                    onChange={(e) => setExtraAliases(e.target.value)}
                    placeholder="Ex: Juarez, J. Sousa"
                    className="mt-2"
                  />
                  <p className="text-sm text-zinc-500 mt-2">
                    Use apenas se você também aparecer com um nome diferente que não foi detectado automaticamente.
                  </p>
                </div>

                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-sm text-zinc-500">
                    Marque um ou mais nomes para o sistema identificar quando falam com você dentro da conversa.
                  </p>
                  <Button onClick={handleSaveIdentity} disabled={savingIdentity} className="rounded-full w-full sm:w-auto">
                    {savingIdentity ? 'Salvando...' : 'Salvar Identidade'}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Summary Card */}
            <Card className="border-zinc-200">
              <CardHeader className="border-b border-zinc-100">
                <div className="flex items-start justify-between gap-4">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <FileText className="w-5 h-5 text-violet-600" />
                    Resumo do Dia
                  </CardTitle>
                  <div className="text-right text-xs text-zinc-500">
                    {usage?.summary?.is_unlimited ? (
                      <span>Ilimitado</span>
                    ) : (
                      <>
                        <p>Hoje: {usage?.summary?.today_used || 0}/{usage?.summary?.today_limit || 2}</p>
                        <p>Mês: {usage?.summary?.month_used || 0}/{usage?.summary?.month_limit || 120}</p>
                      </>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-4 md:p-6">
                <Tabs value={summaryTab} onValueChange={setSummaryTab} className="w-full">
                  <TabsList className="grid w-full grid-cols-2 bg-zinc-50 p-1 rounded-lg mb-4 h-auto">
                    <TabsTrigger value="current" className="rounded-md text-xs sm:text-sm">Resumo atual</TabsTrigger>
                    <TabsTrigger value="history" className="rounded-md text-xs sm:text-sm">Histórico</TabsTrigger>
                  </TabsList>

                  <TabsContent value="current" className="mt-0">
                    {summary ? (
                      <div className="space-y-4">
                        <div className="flex flex-wrap gap-2">
                          <Badge variant="outline" className="border-violet-200 text-violet-700 bg-violet-50">
                            Refere-se a: {formatDateLabel(summary.date || selectedDate)}
                          </Badge>
                          <Badge variant="outline" className="border-zinc-200 text-zinc-600 bg-zinc-50">
                            Gerado em: {formatDateTimeLabel(summary.created_at)}
                          </Badge>
                        </div>
                        <div className="p-4 bg-violet-50 rounded-lg border border-violet-100">
                          <p className="text-sm font-medium text-violet-700">
                            {summary.one_liner}
                          </p>
                        </div>
                        <p className="text-zinc-600 leading-relaxed">
                          {summary.full_summary}
                        </p>
                        <div className="flex justify-end">
                          <Button onClick={handleGenerateSummary} disabled={summarizing} variant="outline" className="rounded-full w-full sm:w-auto">
                            {summarizing ? 'Gerando...' : 'Gerar um novo resumo'}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-zinc-500">Clique em "Gerar resumo" para criar um resumo real desta data.</p>
                    )}
                  </TabsContent>

                  <TabsContent value="history" className="mt-0">
                    {summaryHistory.length > 0 ? (
                      <div className="space-y-3">
                        {summaryHistory.map((item) => (
                          <div key={item.history_id || item.summary_id || `${item.date}-${item.created_at}`} className="rounded-2xl border border-zinc-200 bg-white p-4">
                            <div className="flex flex-wrap gap-2 mb-3">
                              <Badge variant="outline" className="border-violet-200 text-violet-700 bg-violet-50">
                                Data do resumo: {formatDateLabel(item.date)}
                              </Badge>
                              <Badge variant="outline" className="border-zinc-200 text-zinc-600 bg-zinc-50">
                                Gerado em: {formatDateTimeLabel(item.created_at)}
                              </Badge>
                            </div>
                            <p className="text-sm font-medium text-zinc-900">{item.one_liner}</p>
                            <p className="mt-2 text-sm text-zinc-600 leading-relaxed">{item.full_summary}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-zinc-500">Nenhum resumo real foi gerado para este grupo ainda.</p>
                    )}
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>

            <Card className="border-amber-200 bg-gradient-to-br from-amber-50 via-white to-violet-50 shadow-[0_20px_60px_-40px_rgba(139,92,246,0.45)]">
              <CardHeader className="border-b border-amber-100">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <CardTitle className="flex items-center gap-2 text-lg leading-snug">
                    <div className="w-9 h-9 rounded-2xl bg-gradient-to-br from-amber-200 via-amber-100 to-violet-100 flex items-center justify-center shadow-sm">
                      <Stars className="w-5 h-5 text-amber-600" />
                    </div>
                    Pergunte para a Openova.IA
                    <Badge className="bg-white/80 text-amber-700 border border-amber-200">magico</Badge>
                  </CardTitle>
                  <div className="text-left sm:text-right text-xs text-zinc-500">
                    {usage?.ask?.is_unlimited ? (
                      <span>Ilimitado</span>
                    ) : (
                      <>
                        <p>Hoje: {usage?.ask?.today_used || 0}/{usage?.ask?.today_limit || 3}</p>
                        <p>Mês: {usage?.ask?.month_used || 0}/{usage?.ask?.month_limit || 90}</p>
                      </>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-4 md:p-6">
                <Tabs value={askTab} onValueChange={setAskTab} className="w-full">
                  <TabsList className="grid w-full grid-cols-3 bg-white/80 p-1 rounded-lg mb-4 h-auto">
                    <TabsTrigger value="ask" className="rounded-md text-xs sm:text-sm">Perguntar</TabsTrigger>
                    <TabsTrigger value="suggested" className="rounded-md text-xs sm:text-sm">Sugeridas</TabsTrigger>
                    <TabsTrigger value="history" className="rounded-md text-xs sm:text-sm">Histórico</TabsTrigger>
                  </TabsList>

                  <TabsContent value="ask" className="mt-0 space-y-4">
                    <div>
                      <Label htmlFor="askPrompt">Faça uma pergunta sobre o conteúdo deste grupo</Label>
                      <Textarea
                        id="askPrompt"
                        value={askPrompt}
                        onChange={(e) => setAskPrompt(e.target.value)}
                        placeholder="Ex: O que decidiram sobre o Renato? Ex: Quem ficou responsável por responder?"
                        className="mt-2 min-h-[110px]"
                      />
                      <p className="text-sm text-zinc-500 mt-2">
                        A resposta considera todo o historico importado do grupo, com prioridade para as informacoes mais atuais.
                      </p>
                    </div>

                    <div className="flex flex-col gap-4 rounded-2xl border border-amber-100 bg-white/70 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <p className="text-sm font-medium text-zinc-800">Modo focado na minha identidade</p>
                        <p className="text-sm text-zinc-500">Quando ativado, a Openova.IA responde olhando principalmente o que envolve voce no grupo.</p>
                      </div>
                      <Switch checked={identityOnlyAsk} onCheckedChange={setIdentityOnlyAsk} />
                    </div>

                    <div className="flex justify-end">
                      <Button onClick={handleAskGroup} disabled={askingAi} className="rounded-full w-full sm:w-auto">
                        {askingAi ? 'Consultando...' : 'Perguntar à IA'}
                      </Button>
                    </div>

                    {groupAnswerError && (
                      <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                        <div className="flex items-start gap-3">
                          <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-sm font-medium text-amber-800">Openova.IA indisponível no momento</p>
                            <p className="text-sm text-amber-700 mt-1">{groupAnswerError}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {groupAnswer && (
                      <div className="space-y-4">
                        <div className="p-4 rounded-xl border border-violet-100 bg-violet-50">
                          <div className="flex items-center justify-between gap-3 mb-2">
                            <p className="text-sm font-medium text-violet-700">Resposta da IA</p>
                            <div className="flex flex-wrap items-center justify-end gap-2">
                              {groupAnswer.profile && (
                                <Badge className="bg-amber-100 text-amber-700 border-0">
                                  Perfil: {groupAnswer.profile.replaceAll('_', ' ')}
                                </Badge>
                              )}
                              {groupAnswer.identity_only && (
                                <Badge className="bg-violet-100 text-violet-700 border-0">
                                  Modo identidade
                                </Badge>
                              )}
                              <Badge className="bg-white text-violet-700 border border-violet-200">
                                Confiança: {groupAnswer.confidence === 'high' ? 'Alta' : groupAnswer.confidence === 'low' ? 'Baixa' : 'Média'}
                              </Badge>
                            </div>
                          </div>
                          <p className="text-sm text-zinc-700 whitespace-pre-wrap">{groupAnswer.answer}</p>
                        </div>

                        {groupAnswer.improvement_suggestions?.length > 0 && (
                          <div className="space-y-2">
                            <p className="text-sm font-medium text-zinc-700">Sugestões práticas</p>
                            {groupAnswer.improvement_suggestions.map((item, index) => (
                              <div key={index} className="p-3 rounded-lg bg-amber-50 border border-amber-100 text-sm text-zinc-700">
                                {item}
                              </div>
                            ))}
                          </div>
                        )}

                        {groupAnswer.cited_messages?.length > 0 && (
                          <div className="space-y-2">
                            <p className="text-sm font-medium text-zinc-700">Trechos usados na resposta</p>
                            {groupAnswer.cited_messages.map((item, index) => (
                              <div key={index} className="p-3 rounded-lg bg-zinc-50 border border-zinc-200 text-sm text-zinc-600">
                                {item}
                              </div>
                            ))}
                          </div>
                        )}

                        <div className="flex justify-end">
                          <Button variant="outline" onClick={() => copyToClipboard(groupAnswer.answer)} className="rounded-full w-full sm:w-auto">
                            <Copy className="w-4 h-4 mr-2" />
                            Copiar Resposta
                          </Button>
                        </div>
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="suggested" className="mt-0">
                    <div className="space-y-3">
                      {suggestedQuestions.length > 0 ? suggestedQuestions.map((item, index) => (
                        <button
                          key={`${item}-${index}`}
                          type="button"
                          onClick={() => {
                            setAskTab('ask');
                            handleAskGroup(item);
                          }}
                          className="w-full text-left p-4 rounded-xl border border-amber-100 bg-white/80 hover:bg-amber-50 transition-colors"
                        >
                          <p className="text-sm text-zinc-800">{item}</p>
                        </button>
                      )) : (
                        <p className="text-sm text-zinc-500">Ainda nao ha perguntas sugeridas para este grupo.</p>
                      )}
                    </div>
                  </TabsContent>

                  <TabsContent value="history" className="mt-0">
                    <ScrollArea className="h-[320px] sm:h-[360px] pr-2">
                      <div className="space-y-3">
                        {askHistory.length > 0 ? askHistory.map((item) => (
                          <div key={item.ask_id || `${item.question}-${item.created_at}`} className="p-4 rounded-xl border border-zinc-200 bg-white/80">
                            <div className="flex flex-wrap items-center gap-2 mb-2">
                              <Badge className="bg-zinc-100 text-zinc-700 border-0">{item.date}</Badge>
                              {item.identity_only && (
                                <Badge className="bg-violet-100 text-violet-700 border-0">Modo identidade</Badge>
                              )}
                              {item.profile && (
                                <Badge className="bg-amber-100 text-amber-700 border-0">{item.profile.replaceAll('_', ' ')}</Badge>
                              )}
                            </div>
                            <p className="text-sm font-medium text-zinc-900">{item.question}</p>
                            <p className="text-sm text-zinc-600 mt-2 whitespace-pre-wrap">{item.answer}</p>
                          </div>
                        )) : (
                          <p className="text-sm text-zinc-500">As perguntas feitas para este grupo aparecerao aqui.</p>
                        )}
                      </div>
                    </ScrollArea>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>

            {/* Tabs for Details */}
            <Tabs defaultValue="response" className="w-full">
              <TabsList className="grid w-full grid-cols-3 bg-zinc-100 p-1 rounded-lg h-auto">
                <TabsTrigger value="response" className="rounded-md text-[11px] sm:text-sm px-2">
                  <AlertCircle className="w-4 h-4 mr-2" />
                  Precisam Resposta
                </TabsTrigger>
                <TabsTrigger value="decisions" className="rounded-md text-[11px] sm:text-sm px-2">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Decisões
                </TabsTrigger>
                <TabsTrigger value="pending" className="rounded-md text-[11px] sm:text-sm px-2">
                  <Clock className="w-4 h-4 mr-2" />
                  Pendências
                </TabsTrigger>
              </TabsList>

              <TabsContent value="response" className="mt-4">
                <Card className="border-zinc-200">
                  <CardContent className="p-4">
                    {summary?.questions?.length > 0 ? (
                      <div className="space-y-4">
                        {summary.questions.map((item, index) => (
                          <div 
                            key={index}
                            className="p-4 bg-zinc-50 rounded-lg border border-zinc-200"
                          >
                            <div className="flex items-start justify-between gap-4 mb-2">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-zinc-500 font-medium">
                                    {item.sender}
                                  </span>
                                  {item.directed_to_user && (
                                    <Badge className="bg-violet-100 text-violet-700 border-0 text-[10px]">
                                      <AtSign className="w-3 h-3 mr-1" />
                                      Com você
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-zinc-900 mt-1">{item.question}</p>
                              </div>
                              {getPriorityBadge(item.priority)}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-zinc-500">
                        <HelpCircle className="w-8 h-8 mx-auto mb-2 text-zinc-300" />
                        <p>Nenhum item identificado</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="decisions" className="mt-4">
                <Card className="border-zinc-200">
                  <CardContent className="p-4">
                    {summary?.decisions?.length > 0 ? (
                      <ul className="space-y-3">
                        {summary.decisions.map((decision, index) => (
                          <li key={index} className="flex items-start gap-3 p-3 bg-emerald-50 rounded-lg">
                            <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                            <span className="text-zinc-700">{decision}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="text-center py-8 text-zinc-500">
                        <CheckCircle className="w-8 h-8 mx-auto mb-2 text-zinc-300" />
                        <p>Nenhuma decisão registrada</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="pending" className="mt-4">
                <Card className="border-zinc-200">
                  <CardContent className="p-4">
                    {summary?.pending_items?.length > 0 ? (
                      <ul className="space-y-3">
                        {summary.pending_items.map((item, index) => (
                          <li key={index} className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg">
                            <Clock className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                            <span className="text-zinc-700">{item}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="text-center py-8 text-zinc-500">
                        <Clock className="w-8 h-8 mx-auto mb-2 text-zinc-300" />
                        <p>Nenhuma pendência identificada</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>

          {/* Right Column - Response Suggestions */}
          <div className="lg:col-span-4">
            <Card className="border-zinc-200 lg:sticky lg:top-6">
              <CardHeader className="border-b border-zinc-100">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <MessageSquare className="w-5 h-5 text-violet-600" />
                  Sugestões de Resposta
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <ScrollArea className="h-[320px] sm:h-[420px] lg:h-[500px] pr-2">
                  {summary?.response_suggestions?.length > 0 ? (
                    <div className="space-y-6">
                      {summary.response_suggestions.map((suggestion, index) => (
                        <div key={index} className="space-y-3">
                          <p className="text-sm text-zinc-600 font-medium line-clamp-2">
                            Para: "{suggestion?.question?.substring(0, 50) || 'Mensagem'}..."
                          </p>
                          <div className="space-y-2">
                            {normalizeSuggestionOptions(suggestion?.options).map((option, optIndex) => (
                              <div 
                                key={optIndex}
                                className="suggestion-card p-3 rounded-lg"
                                onClick={() => copyToClipboard(option?.text || '')}
                                data-testid={`copy-suggestion-${index}-${optIndex}`}
                              >
                                <div className="flex items-start justify-between gap-2">
                                  <div className="flex-1">
                                    <Badge variant="outline" className="text-[10px] mb-1 bg-zinc-100 border-0">
                                      {option?.type || 'resposta'}
                                    </Badge>
                                    <p className="text-sm text-zinc-700">{option?.text || ''}</p>
                                  </div>
                                  <Copy className="w-4 h-4 text-zinc-400 flex-shrink-0" />
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-zinc-500">
                      <Sparkles className="w-8 h-8 mx-auto mb-2 text-zinc-300" />
                      <p className="text-sm">Gere um resumo para ver sugestões de resposta</p>
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Recent Messages */}
        <div className="mt-8">
          <h2 className="font-heading text-lg font-semibold text-zinc-900 mb-4">
            Mensagens Recentes
          </h2>
          <Card className="border-zinc-200">
            <CardContent className="p-4">
              <ScrollArea className="h-[260px] sm:h-[300px]">
                <div className="space-y-3">
                  {messages.slice(0, 20).map((message, index) => (
                    <div 
                      key={message.message_id || index}
                      className={`p-3 rounded-lg ${
                        message.directed_to_user
                          ? 'bg-violet-50 border border-violet-100'
                          : message.needs_response
                            ? 'bg-orange-50 border border-orange-100'
                            : 'bg-zinc-50'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <span className="text-xs font-medium text-zinc-500">
                          {message.sender}
                        </span>
                        <div className="flex items-center gap-2">
                          {message.directed_to_user && (
                            <Badge className="text-[10px] bg-violet-100 text-violet-700 border-0">
                              Falaram com você
                            </Badge>
                          )}
                          {message.needs_response && (
                            <Badge className="text-[10px] bg-orange-100 text-orange-700 border-0">
                              Responder
                            </Badge>
                          )}
                        </div>
                      </div>
                      <p className="text-sm text-zinc-700">{message.content}</p>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </div>
    </AppLayout>
  );
}
